import { PageHeading } from "@/components/ui/page";
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Save, Search, Package } from "lucide-react";
import { salesAPI, productsAPI, branchesAPI, posAPI, getApiErrorPayload } from "@/lib/api";
import { useAuth } from "@/features/auth/contexts/AuthContext";
import type { Pos } from "@/types";
import { createSaleSchema } from "@/lib/schemas/sale.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { getDashboardProductImageSrc } from "@/lib/productImageUrl";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { SectionHead } from "@/components/ui/section-head";
import { CustomerPicker } from "@/features/customers/components/CustomerPicker";
import { useUnsavedChangesGuard, useIsDirty } from "@/hooks/useUnsavedChangesGuard";
import { FieldError, firstFieldErrors } from "@/components/ui/field-error";

interface SalesItem {
	id: string;
	productId: string;
	productName: string;
	productImage?: string;
	barcode?: string;
	quantity: number;
	unitPrice: number;
	discountPercentage: number;
	discountAmount: number;
	taxPercentage: number;
	taxAmount: number;
	totalAmount: number;
}

interface SalesForm {
	customerId: string;
	notes: string;
	items: SalesItem[];
}

/**
 * Unified line math (matches the backend):
 * discount = subtotal * discountRate / 100; tax = (subtotal - discount) * taxRate / 100;
 * total = subtotal - discount + tax (tax applied AFTER discount).
 */
const recalcItem = (item: SalesItem): SalesItem => {
	const lineSubtotal = item.unitPrice * item.quantity;
	const discountAmount = (lineSubtotal * item.discountPercentage) / 100;
	const taxAmount = ((lineSubtotal - discountAmount) * item.taxPercentage) / 100;
	const totalAmount = lineSubtotal - discountAmount + taxAmount;
	return { ...item, discountAmount, taxAmount, totalAmount };
};

const AddSalesPage: React.FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { toast } = useToast();
	const { user } = useAuth();
	const currency = useCurrency();
	const [saving, setSaving] = useState(false);
	const [products, setProducts] = useState<any[]>([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filteredProducts, setFilteredProducts] = useState<any[]>([]);
	const [selectedProducts, setSelectedProducts] = useState<SalesItem[]>([]);
	const [branches, setBranches] = useState<{ id: number; name: string }[]>([]);
	const [branchId, setBranchId] = useState<number | "">("");
	const [posList, setPosList] = useState<Pos[]>([]);
	const [posId, setPosId] = useState<string>("");
	const [tableNumber, setTableNumber] = useState("");
	const [salesForm, setSalesForm] = useState<SalesForm>({
		customerId: "",
		notes: "",
		items: [],
	});
	const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
	// Branch is preselected after loading, so it is not part of the tracked form state.
	const { isDirty } = useIsDirty({ salesForm, selectedProducts, posId, tableNumber });
	const { allowNavigation } = useUnsavedChangesGuard(isDirty && !saving);

	const clearFieldError = (field: string) => {
		setFieldErrors(prev => {
			if (!prev[field]) return prev;
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	useEffect(() => {
		branchesAPI.getAll().then((list: any) => {
			const b = Array.isArray(list) ? list : [];
			setBranches(b);
			if (!b.length) return;
			setBranchId(prev => {
				if (prev !== "") return prev;
				const uid = user?.branchId;
				return uid && b.some((x: { id: number }) => x.id === uid) ? uid : b[0].id;
			});
		});
	}, [user?.branchId]);

	useEffect(() => {
		if (!branchId) {
			setProducts([]);
			setFilteredProducts([]);
			setPosList([]);
			return;
		}
		productsAPI.getAll({ branchId, limit: 1000, page: 1 }).then((data: any) => {
			const list = data?.products ?? [];
			setProducts(list);
			setFilteredProducts(list);
		});
		posAPI.getAll({ branchId, limit: 100, page: 1 }).then((res: any) => {
			setPosList(res?.pos ?? []);
		});
	}, [branchId]);

	useEffect(() => {
		if (searchQuery.trim() === "") {
			setFilteredProducts(products);
		} else {
			const filtered = products.filter(
				product =>
					product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
					product.barcode?.toLowerCase().includes(searchQuery.toLowerCase()),
			);
			setFilteredProducts(filtered);
		}
	}, [searchQuery, products]);

	const handleInputChange = (field: keyof SalesForm, value: any) => {
		setSalesForm(prev => ({ ...prev, [field]: value }));
	};

	const addProductToSale = (product: any) => {
		clearFieldError("items");
		const existingItem = selectedProducts.find(item => item.productId === product.id);

		if (existingItem) {
			setSelectedProducts(prev =>
				prev.map(item =>
					item.productId === product.id ? recalcItem({ ...item, quantity: item.quantity + 1 }) : item,
				),
			);
		} else {
			const newItem: SalesItem = recalcItem({
				id: Date.now().toString(),
				productId: product.id,
				productName: product.name,
				productImage: product.image,
				barcode: product.barcode,
				quantity: 1,
				unitPrice: product.sellingPrice || 0,
				discountPercentage: 0,
				discountAmount: 0,
				taxPercentage: Number(product.taxRate) || 0,
				taxAmount: 0,
				totalAmount: product.sellingPrice || 0,
			});
			setSelectedProducts(prev => [...prev, newItem]);
		}

		setSearchQuery("");
		setFilteredProducts(products);
	};

	const updateItem = (itemId: string, field: keyof SalesItem, value: any) => {
		setSelectedProducts(prev =>
			prev.map(item => (item.id === itemId ? recalcItem({ ...item, [field]: value }) : item)),
		);
	};

	const removeItem = (itemId: string) => {
		setSelectedProducts(prev => prev.filter(item => item.id !== itemId));
	};

	const calculateTotals = () => {
		const subtotal = selectedProducts.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0);
		const totalDiscount = selectedProducts.reduce((sum, item) => sum + item.discountAmount, 0);
		const totalTax = selectedProducts.reduce((sum, item) => sum + item.taxAmount, 0);
		const total = subtotal - totalDiscount + totalTax;

		return { subtotal, totalDiscount, totalTax, total };
	};

	const handleSubmit = async () => {
		const errors: Record<string, string> = {};
		if (!branchId) errors.branchId = "validation.required";
		if (selectedProducts.length === 0) errors.items = "sales.errors.productsRequired";
		if (Object.keys(errors).length > 0) {
			setFieldErrors(errors);
			toast({
				title: t("common.error"),
				description: t("validation.checkFields"),
				variant: "destructive",
			});
			return;
		}
		setFieldErrors({});

		try {
			setSaving(true);
			const { subtotal: sub, total } = calculateTotals();
			const itemsForApi = selectedProducts.map(item => {
				const quantity = Number(item.quantity) || 1;
				const unitPrice = Number(item.unitPrice);
				const taxRate = Number(item.taxPercentage) || 0;
				const discountRate = Number(item.discountPercentage) || 0;
				if (!Number.isFinite(unitPrice) || unitPrice < 0 || !Number.isFinite(quantity) || quantity <= 0) {
					throw new Error(t("sales.errors.invalidItemAmounts"));
				}
				return {
					productId: Number(item.productId),
					quantity,
					unitPrice,
					taxRate,
					discountRate,
				};
			});
			const saleData = {
				customerId: salesForm.customerId ? Number(salesForm.customerId) : undefined,
				branchId: branchId ? Number(branchId) : undefined,
				posId: posId ? Number(posId) : undefined,
				tableNumber: tableNumber.trim() || undefined,
				notes: salesForm.notes,
				items: itemsForApi,
				subtotal: sub,
				totalAmount: total,
				orderSource: "admin" as const,
			};

			const parsed = createSaleSchema.safeParse(saleData);
			if (!parsed.success) {
				setFieldErrors(firstFieldErrors(parsed.error.issues));
				toast({
					title: t("common.error"),
					description: t("validation.checkFields"),
					variant: "destructive",
				});
				return;
			}

			await salesAPI.create(parsed.data);
			toast({
				title: t("common.success"),
				description: t("sales.messages.created"),
			});
			allowNavigation();
			navigate("/sales");
		} catch (error) {
			const payload = getApiErrorPayload(error);
			const errorMessage = t(payload.key, (payload.params ?? {}) as Record<string, string>);
			toast({
				title: t("common.error"),
				description: errorMessage || t("common.errors.unexpected"),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const { subtotal, totalDiscount, totalTax, total } = calculateTotals();
	const selectedPos = posList.find(p => String(p.id) === posId);
	const showTableField = !!selectedPos?.offersTableService;

	return (
		<div className="container mx-auto py-6 space-y-6">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-4">
					<div>
						<PageHeading className="mb-2 ">{t("sales.add.title")}</PageHeading>
						<p className="text-muted-foreground">{t("sales.add.description")}</p>
					</div>
				</div>
				<Button onClick={handleSubmit} disabled={saving}>
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white me-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("sales.add.save")}
				</Button>
			</div>

			<div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
				{/* Sales Form */}
				<div className="lg:col-span-2 space-y-6">
					{/* Operation Data */}
					<SectionHead num={1} title={t("sales.add.operationData")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							<p className="text-sm text-muted-foreground">{t("sales.add.createdAsPaidNote")}</p>
							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>
										{t("sales.add.branch")} <span className="text-destructive">*</span>
									</Label>
									<Select
										value={branchId ? String(branchId) : ""}
										onValueChange={v => {
											setBranchId(v ? parseInt(v, 10) : "");
											setPosId("");
											setTableNumber("");
											clearFieldError("branchId");
										}}
									>
										<SelectTrigger
											aria-label={t("sales.add.branch")}
											aria-invalid={!!fieldErrors.branchId}
										>
											<SelectValue placeholder={t("sales.add.selectBranch")} />
										</SelectTrigger>
										<SelectContent>
											{branches.map(b => (
												<SelectItem key={b.id} value={String(b.id)}>
													{b.name}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
									<FieldError message={fieldErrors.branchId} />
								</div>
								<div className="space-y-2">
									<Label htmlFor="sale-customer">{t("sales.add.customerOptional")}</Label>
									{/* No customer = walk-in sale, listed as "Cash Customer" in sales. */}
									<CustomerPicker
										id="sale-customer"
										value={salesForm.customerId}
										onChange={value => handleInputChange("customerId", value)}
										placeholder={t("sales.card.cashCustomer")}
										clearable
									/>
								</div>
							</div>

							<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
								<div className="space-y-2">
									<Label>{t("sales.add.posOptional")}</Label>
									<Select
										value={posId ? posId : "__none__"}
										onValueChange={v => {
											setPosId(v === "__none__" ? "" : v);
											setTableNumber("");
										}}
									>
										<SelectTrigger aria-label={t("sales.add.posOptional")}>
											<SelectValue placeholder={t("sales.add.selectPosOptional")} />
										</SelectTrigger>
										<SelectContent>
											<SelectItem value="__none__">{t("sales.add.noPos")}</SelectItem>
											{posList.map(p => (
												<SelectItem key={p.id} value={String(p.id)}>
													{p.name} ({p.code})
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								</div>
								{showTableField && (
									<div className="space-y-2">
										<Label>{t("sales.add.tableNumber")}</Label>
										<Input
											aria-label={t("sales.add.tableNumber")}
											value={tableNumber}
											onChange={e => setTableNumber(e.target.value)}
											placeholder={t("sales.add.tablePlaceholder")}
											maxLength={32}
										/>
									</div>
								)}
							</div>

							<div className="space-y-2">
								<Label>{t("sales.add.notes")}</Label>
								<Input
									aria-label={t("sales.add.notes")}
									type="text"
									value={salesForm.notes}
									onChange={e => handleInputChange("notes", e.target.value)}
									placeholder={t("sales.add.notesPlaceholder")}
								/>
							</div>
						</CardContent>
					</Card>

					{/* Products Selection */}
					<SectionHead num={2} title={t("sales.add.products")} hint={t("sales.add.productsDescription")} />
					<Card>
						<CardContent className="space-y-6 pt-6">
							{/* Product Search */}
							<div className="space-y-2">
								<Label>{t("sales.add.searchProducts")}</Label>
								<div className="relative">
									<Search className="absolute start-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
									<Input
										aria-label={t("sales.add.searchProducts")}
										placeholder={t("sales.add.searchPlaceholder")}
										value={searchQuery}
										onChange={e => setSearchQuery(e.target.value)}
										className="ps-10"
									/>
								</div>
							</div>

							{/* Product list: all when search empty, filtered when typing */}
							<div className="border rounded-lg p-4 max-h-60 overflow-y-auto">
								<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
									{filteredProducts.map(product => (
										<div
											key={product.id}
											className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted cursor-pointer"
											onClick={() => addProductToSale(product)}
										>
											<div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden">
												{getDashboardProductImageSrc(product.image) ? (
													<img
														src={getDashboardProductImageSrc(product.image)}
														alt={product.name}
														className="w-full h-full object-cover rounded-lg"
													/>
												) : (
													<Package className="h-6 w-6 text-primary" />
												)}
											</div>
											<div className="flex-1">
												<h4 className="font-medium">{product.name}</h4>
												<p className="text-sm text-muted-foreground">
													{product.barcode &&
														`${t("products.table.barcode")}: ${product.barcode}`}
												</p>
												<p className="text-sm font-medium">
													{t("sales.add.price")}:{" "}
													<Price value={product.sellingPrice || 0} currency={currency} />
												</p>
											</div>
											<Button
												title={t("common.add")}
												aria-label={t("common.add")}
												size="sm"
												variant="outline"
											>
												<Plus className="h-4 w-4" />
											</Button>
										</div>
									))}
								</div>
							</div>

							{/* Selected Products */}
							<div className="space-y-4">
								<h4 className="font-medium">
									{t("sales.add.selectedProducts")} <span className="text-destructive">*</span>
								</h4>
								<FieldError message={fieldErrors.items} />
								{selectedProducts.map(item => (
									<div key={item.id} className="flex items-center gap-4 p-4 border rounded-lg">
										<div className="w-16 h-16 bg-primary/10 rounded-lg flex items-center justify-center overflow-hidden">
											{getDashboardProductImageSrc(item.productImage) ? (
												<img
													src={getDashboardProductImageSrc(item.productImage)}
													alt={item.productName}
													className="w-full h-full object-cover rounded-lg"
												/>
											) : (
												<Package className="h-8 w-8 text-primary" />
											)}
										</div>
										<div className="flex-1">
											<h4 className="font-medium">{item.productName}</h4>
											<div className="grid grid-cols-2 sm:grid-cols-5 gap-4 mt-2">
												<div>
													<Label className="text-sm">{t("sales.add.quantity")}</Label>
													<Input
														aria-label={t("sales.add.quantity")}
														type="number"
														min="1"
														value={item.quantity}
														onChange={e =>
															updateItem(
																item.id,
																"quantity",
																parseInt(e.target.value) || 1,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("sales.add.unitPrice")}</Label>
													<Input
														aria-label={t("sales.add.unitPrice")}
														type="number"
														min="0"
														step="0.01"
														value={item.unitPrice}
														onChange={e =>
															updateItem(
																item.id,
																"unitPrice",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-24"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("sales.add.discount")}</Label>
													<Input
														aria-label={t("sales.add.discount")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.discountPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"discountPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("sales.add.taxPercent")}</Label>
													<Input
														aria-label={t("sales.add.taxPercent")}
														type="number"
														min="0"
														max="100"
														step="0.01"
														value={item.taxPercentage}
														onChange={e =>
															updateItem(
																item.id,
																"taxPercentage",
																parseFloat(e.target.value) || 0,
															)
														}
														className="w-20"
													/>
												</div>
												<div>
													<Label className="text-sm">{t("sales.add.total")}</Label>
													<div className="text-lg font-semibold">
														<Price value={item.totalAmount} currency={currency} />
													</div>
												</div>
											</div>
										</div>
										<Button
											title={t("common.delete")}
											aria-label={t("common.delete")}
											size="sm"
											variant="destructive"
											onClick={() => removeItem(item.id)}
										>
											<Trash2 className="h-4 w-4 text-white" />
										</Button>
									</div>
								))}
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Summary */}
				<div className="space-y-6">
					<Card>
						<CardHeader>
							<CardTitle>{t("sales.add.summary")}</CardTitle>
						</CardHeader>
						<CardContent className="space-y-4">
							<div className="flex justify-between">
								<span>{t("sales.add.subtotal")}</span>
								<span className="font-semibold">
									<Price value={subtotal} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("sales.add.discount")}</span>
								<span className="font-semibold text-destructive">
									-<Price value={totalDiscount} currency={currency} />
								</span>
							</div>
							<div className="flex justify-between">
								<span>{t("sales.add.tax")}</span>
								<span className="font-semibold">
									<Price value={totalTax} currency={currency} />
								</span>
							</div>
							<div className="border-t pt-4">
								<div className="flex justify-between text-lg font-bold">
									<span>{t("sales.add.total")}</span>
									<Price value={total} currency={currency} />
								</div>
							</div>
						</CardContent>
					</Card>
				</div>
			</div>

			{/* Sticky action bar */}
			<div className="sticky bottom-0 z-10 flex items-center justify-end gap-2 rounded-t-lg border-t bg-background/95 px-3 py-3 backdrop-blur">
				<Button type="button" variant="outline" onClick={() => navigate(-1)} disabled={saving}>
					{t("common.cancel")}
				</Button>
				<Button onClick={handleSubmit} disabled={saving} className="w-full sm:w-40">
					{saving ? (
						<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mx-2"></div>
					) : (
						<Save className="h-4 w-4 mx-2" />
					)}
					{t("common.save")}
				</Button>
			</div>
		</div>
	);
};

export default AddSalesPage;
